import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import './Feed.css';

const Feed = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [followingStatus, setFollowingStatus] = useState({});
  const observerTarget = useRef(null);

  // Post creation state
  const [postContent, setPostContent] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [postImagePreviews, setPostImagePreviews] = useState([]);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [postError, setPostError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activityImageIndices, setActivityImageIndices] = useState({});
  const fileInputRef = useRef(null);
  
  // Report state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState(false);
  
  const MAX_POST_LENGTH = 500;
  const MAX_IMAGES = 4;

  // Fetch unread counts
  const fetchUnreadCounts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      const [notifRes, msgRes] = await Promise.all([
        fetch('/api/notifications/unread-count', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/messages/unread-count', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (notifRes.ok) {
        const notifData = await notifRes.json();
        setUnreadNotifications(notifData.count || 0);
      }
      if (msgRes.ok) {
        const msgData = await msgRes.json();
        setUnreadMessages(msgData.count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread counts:', err);
    }
  }, [isAuthenticated]);

  // Fetch activities with infinite scroll
  const fetchActivities = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`/api/feed?page=${pageNum}&limit=15`, {
        headers
      });

      const data = await response.json();

      if (data.success) {
        const newActivities = data.data || [];
        
        if (append) {
          setActivities(prev => [...prev, ...newActivities]);
        } else {
          setActivities(newActivities);
        }

        // Check if there are more items
        const totalPages = data.pagination?.pages || 1;
        setHasMore(pageNum < totalPages);
      } else {
        setError(data.message || 'Failed to load feed');
      }
    } catch (err) {
      console.error('Fetch activities error:', err);
      setError('Failed to load feed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchActivities(1, false);
    // Fetch unread counts once on mount (Navbar/NotificationBell will keep them updated via polling)
    fetchUnreadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchActivities]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchActivities(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, page, fetchActivities]);

  const getActivityIcon = (activityType) => {
    const icons = {
      showcase_created: '🎨',
      giveaway_created: '🎁',
      indie_game_created: '🎮',
      showcase_comment: '💬',
      discount_created: '🏷️',
      list_created: '📝',      post_created: '📄',      achievement_unlocked: '🏆',
      review_posted: '⭐'
    };
    return icons[activityType] || '📰';
  };

  const getActivityColor = (activityType) => {
    const colors = {
      showcase_created: '#9333ea',
      giveaway_created: '#dc2626',
      indie_game_created: '#2563eb',
      showcase_comment: '#16a34a',
      discount_created: '#ea580c',
      list_created: '#eab308',
      post_created: '#06b6d4',
      achievement_unlocked: '#f59e0b',
      review_posted: '#8b5cf6'
    };
    return colors[activityType] || 'var(--accent)';
  };

  const getActivityLink = (activity) => {
    switch (activity.activityType) {
      case 'showcase_created':
      case 'showcase_comment':
        return `/profile/${activity.userId?.username}?tab=showcase`;
      case 'post_created':
        return `/posts/${activity.referenceId?._id || activity.referenceId}`;
      case 'giveaway_created':
        return `/giveaways`;
      case 'indie_game_created':
        return `/indie-games`;
      case 'discount_created':
        return `/discounts`;
      case 'list_created':
        return activity.referenceId?._id 
          ? `/lists/${activity.referenceId._id}` 
          : `/discover-lists`;
      default:
        return '#';
    }
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const SidebarButton = ({ icon, label, badge, onClick, to, color = 'var(--accent)' }) => {
    const content = (
      <div className="sidebar-btn" style={{ '--btn-color': color }}>
        <span className="sidebar-btn-icon">{icon}</span>
        <span className="sidebar-btn-label">{label}</span>
        {badge > 0 && <span className="sidebar-btn-badge">{badge > 99 ? '99+' : badge}</span>}
      </div>
    );

    return to ? <Link to={to}>{content}</Link> : <div onClick={onClick}>{content}</div>;
  };

  // Change activity image carousel
  const changeActivityImage = (activityId, direction) => {
    setActivityImageIndices(prev => {
      const currentIndex = prev[activityId] || 0;
      const activity = activities.find(a => a._id === activityId);
      if (!activity?.metadata?.images) return prev;
      
      const maxIndex = activity.metadata.images.length - 1;
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
      } else {
        newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
      }

      return { ...prev, [activityId]: newIndex };
    });
  };

  // Handle post image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + postImages.length > MAX_IMAGES) {
      setPostError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    setPostImages(prev => [...prev, ...files]);
    
    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPostImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
    setPostError('');
  };

  // Remove image
  const removeImage = (index) => {
    setPostImages(prev => prev.filter((_, i) => i !== index));
    setPostImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Insert emoji
  const insertEmoji = (emoji) => {
    setPostContent(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Handle report post
  const handleReportPost = (postId) => {
    setReportPostId(postId);
    setReportModalOpen(true);
    setReportError('');
    setReportSuccess(false);
    setReportReason('');
    setReportDescription('');
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportReason || !reportDescription.trim()) {
      setReportError('Please select a reason and provide details');
      return;
    }

    try {
      setReportSubmitting(true);
      setReportError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/posts/${reportPostId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: reportReason,
          description: reportDescription
        })
      });

      const data = await response.json();

      if (response.ok) {
        setReportSuccess(true);
        setTimeout(() => {
          setReportModalOpen(false);
          setReportPostId(null);
          setReportSuccess(false);
        }, 2000);
      } else {
        setReportError(data.message || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Report error:', error);
      setReportError('Failed to submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  // Submit post
  const handlePostSubmit = async () => {
    if (!postContent.trim() && postImages.length === 0) {
      setPostError('Post must have content or images');
      return;
    }

    if (postContent.length > MAX_POST_LENGTH) {
      setPostError(`Post exceeds maximum length of ${MAX_POST_LENGTH} characters`);
      return;
    }

    try {
      setPostSubmitting(true);
      setPostError('');

      const formData = new FormData();
      formData.append('content', postContent);
      postImages.forEach(image => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('token');
      await axios.post('/api/posts', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Reset form
      setPostContent('');
      setPostImages([]);
      setPostImagePreviews([]);
      
      // Refresh feed
      setPage(1);
      await fetchActivities(1, false);
      
    } catch (err) {
      setPostError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setPostSubmitting(false);
    }
  };

  // Handle follow/unfollow
  const handleFollowToggle = async (userId, isCurrentlyFollowing) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (isCurrentlyFollowing) {
        await axios.delete(`/api/follow/${userId}/unfollow`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`/api/follow/${userId}/follow`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      setFollowingStatus(prev => ({
        ...prev,
        [userId]: !isCurrentlyFollowing
      }));
    } catch (err) {
      console.error('Follow toggle error:', err);
    }
  };

  // Fetch following status for all users
  useEffect(() => {
    if (!isAuthenticated || activities.length === 0) return;

    const fetchFollowingStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const userIds = [...new Set(activities.map(a => a.userId?._id).filter(Boolean))];
        
        const statusPromises = userIds.map(async (userId) => {
          if (userId === user._id) return { userId, isFollowing: false };
          
          try {
            const response = await axios.get(`/api/follow/${userId}/follow-status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            return { userId, isFollowing: response.data.data.isFollowing };
          } catch {
            return { userId, isFollowing: false };
          }
        });

        const results = await Promise.all(statusPromises);
        const statusMap = {};
        results.forEach(({ userId, isFollowing }) => {
          statusMap[userId] = isFollowing;
        });
        setFollowingStatus(statusMap);
      } catch (err) {
        console.error('Error fetching following status:', err);
      }
    };

    fetchFollowingStatus();
  }, [activities, isAuthenticated, user]);

  return (
    <div className="feed-container">
      {/* Left Sidebar */}
      <aside className="feed-sidebar">
        <div className="sidebar-header">
          <h2>📰 My Feed</h2>
        </div>

        <nav className="sidebar-nav">
          <SidebarButton 
            icon="🔔" 
            label="Notifications" 
            badge={unreadNotifications}
            to="/notifications"
            color="#f59e0b"
          />
          <SidebarButton 
            icon="✉️" 
            label="Messages" 
            badge={unreadMessages}
            to="/messages"
            color="#3b82f6"
          />
          <SidebarButton 
            icon="🎮" 
            label="Indie Developers" 
            to="/indie-games"
            color="#8b5cf6"
          />
          <SidebarButton 
            icon="👥" 
            label="Community Cards" 
            to="/community"
            color="#10b981"
          />
          <SidebarButton 
            icon="🎁" 
            label="Giveaways" 
            to="/giveaways"
            color="#ef4444"
          />
          <SidebarButton 
            icon="🏷️" 
            label="Discounts" 
            to="/discounts"
            color="#f97316"
          />
          <SidebarButton 
            icon="📝" 
            label="Game Lists" 
            to="/discover-lists"
            color="#eab308"
          />
          <SidebarButton 
            icon="🏆" 
            label="Leaderboard" 
            to="/leaderboard"
            color="#fbbf24"
          />
          <SidebarButton 
            icon="⭐" 
            label="Game Reviews" 
            to="/reviews"
            color="#a855f7"
          />
          <SidebarButton 
            icon="🛡️" 
            label="Scammer List" 
            to="/scammers"
            color="#dc2626"
          />
        </nav>

        {isAuthenticated && (
          <div className="sidebar-footer">
            <div className="sidebar-user-card">
              <div className="sidebar-user-avatar">
                {user?.profile?.avatar?.url ? (
                  <img src={user.profile.avatar.url} alt={user.username} />
                ) : (
                  <div className="avatar-placeholder">
                    {user?.username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user?.username}</div>
                <div className="sidebar-user-points">⭐ {user?.activityPoints?.total || 0} pts</div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Feed */}
      <main className="feed-main">
        <div className="feed-header">
          <h1>Latest Activity</h1>
          <p>See what's happening in the community</p>
        </div>

        {/* Post Creation Box */}
        {isAuthenticated && (
          <div className="create-post-box">
            <div className="create-post-header">
              <div className="create-post-avatar">
                {user?.profile?.avatar?.url ? (
                  <img src={user.profile.avatar.url} alt={user.username} />
                ) : (
                  <div className="avatar-placeholder">
                    {user?.username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>
              <textarea
                className="create-post-textarea"
                placeholder="What's on your mind?"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                maxLength={MAX_POST_LENGTH}
                rows="3"
              />
            </div>

            {postImagePreviews.length > 0 && (
              <div className="create-post-images">
                {postImagePreviews.map((preview, index) => (
                  <div key={index} className="create-post-image-preview">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {postError && (
              <div className="create-post-error">{postError}</div>
            )}

            <div className="create-post-footer">
              <div className="create-post-actions">
                <button
                  className="post-action-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={postImages.length >= MAX_IMAGES}
                  title="Add images"
                >
                  🖼️ Images
                </button>
                <button
                  className="post-action-btn"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  title="Add emoji"
                >
                  😊 Emoji
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {showEmojiPicker && (
                <div className="emoji-picker">
                  {['😀', '😂', '😍', '🎮', '🎯', '🔥', '⭐', '💪', '👍', '❤️', '🎉', '🚀', '💯', '👏', '🎊'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => insertEmoji(emoji)}
                      className="emoji-btn"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <div className="create-post-submit-area">
                <span className="character-count">
                  {postContent.length}/{MAX_POST_LENGTH}
                </span>
                <button
                  className="post-submit-btn"
                  onClick={handlePostSubmit}
                  disabled={postSubmitting || (!postContent.trim() && postImages.length === 0)}
                >
                  {postSubmitting ? 'Posting...' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && page === 1 ? (
          <div className="feed-loading">
            <div className="spinner" />
            <p>Loading feed...</p>
          </div>
        ) : error ? (
          <div className="feed-error">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
            <button onClick={() => fetchActivities(1, false)} className="btn-retry">
              Try Again
            </button>
          </div>
        ) : activities.length === 0 ? (
          <div className="feed-empty">
            <div className="empty-icon">📭</div>
            <h3>No activity yet</h3>
            <p>Follow users or start exploring to see activity here!</p>
            {isAuthenticated && (
              <div className="empty-actions">
                <Link to="/community">
                  <button className="btn-primary">Explore Community</button>
                </Link>
                <Link to="/indie-games">
                  <button className="btn-secondary">Browse Games</button>
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="feed-items">
              {activities.map((activity, index) => (
                <React.Fragment key={activity._id}>
                  <article 
                  key={activity._id} 
                  className="feed-item"
                  style={{ '--activity-color': getActivityColor(activity.activityType) }}
                >
                  <div className="feed-item-border" />
                  
                  <div className="feed-item-content">
                    <div className="feed-item-header">
                      <Link 
                        to={`/profile/${activity.userId?.username}`}
                        className="feed-item-avatar"
                      >
                        {activity.userId?.profile?.avatar?.url ? (
                          <img src={activity.userId.profile.avatar.url} alt={activity.userId.username} />
                        ) : (
                          <div className="avatar-placeholder">
                            {activity.userId?.username?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </Link>

                      <div className="feed-item-meta">
                        <div className="feed-item-user">
                          <Link 
                            to={`/profile/${activity.userId?.username}`}
                            className="username"
                          >
                            {activity.userId?.username}
                          </Link>
                          {activity.userId?.accountType && (
                            <span className={`badge-${activity.userId.accountType}`}>
                              {activity.userId.accountType === 'developer' ? '🎮 Dev' : '✨'}
                            </span>
                          )}
                          <span className="separator">·</span>
                          <span className="timestamp">{formatTimeAgo(activity.createdAt)}</span>
                          
                          {/* Follow Button */}
                          {isAuthenticated && activity.userId?._id !== user._id && (
                            <button
                              className={`feed-follow-btn ${followingStatus[activity.userId._id] ? 'following' : ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleFollowToggle(activity.userId._id, followingStatus[activity.userId._id]);
                              }}
                            >
                              {followingStatus[activity.userId._id] ? '✓ Following' : '+ Follow'}
                            </button>
                          )}
                          
                          {/* Report Button */}
                          {isAuthenticated && activity.userId?._id !== user._id && activity.activityType === 'post_created' && activity.referenceId && (
                            <button
                              className="feed-report-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleReportPost(activity.referenceId._id || activity.referenceId);
                              }}
                              title="Report post"
                            >
                              ⚠️
                            </button>
                          )}
                        </div>
                        
                        <div className="feed-item-type">
                          <span className="type-icon">{getActivityIcon(activity.activityType)}</span>
                          <span className="type-text">
                            {activity.activityType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="feed-item-body">
                      <h3 className="feed-item-title">{activity.title}</h3>
                      
                      {activity.description && (
                        <p className="feed-item-description">
                          {activity.description.length > 200 
                            ? activity.description.substring(0, 200) + '...'
                            : activity.description
                          }
                        </p>
                      )}

                      {activity.metadata?.images && activity.metadata.images.length > 0 && (
                        <div style={{ marginTop: '12px', marginBottom: '12px', position: 'relative' }}>
                          <img 
                            src={activity.metadata.images[activityImageIndices[activity._id] || 0].url} 
                            alt="Post" 
                            style={{
                              width: '100%',
                              maxHeight: '300px',
                              objectFit: 'cover',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                          />
                          {activity.metadata.images.length > 1 && (
                            <>
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '0 10px',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none'
                              }}>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    changeActivityImage(activity._id, 'prev');
                                  }}
                                  style={{
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    fontSize: '1.1rem',
                                    pointerEvents: 'auto',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.9)'}
                                  onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                                >
                                  ←
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    changeActivityImage(activity._id, 'next');
                                  }}
                                  style={{
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    fontSize: '1.1rem',
                                    pointerEvents: 'auto',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.9)'}
                                  onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                                >
                                  →
                                </button>
                              </div>
                              <div style={{
                                position: 'absolute',
                                bottom: '10px',
                                right: '10px',
                                background: 'rgba(0, 0, 0, 0.7)',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '0.85rem',
                                color: 'white'
                              }}>
                                {(activityImageIndices[activity._id] || 0) + 1} / {activity.metadata.images.length}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {activity.metadata && (
                        <div className="feed-item-tags">
                          {activity.metadata.platform && (
                            <span className="tag tag-platform">{activity.metadata.platform}</span>
                          )}
                          {activity.metadata.discountPercentage && (
                            <span className="tag tag-discount">
                              {activity.metadata.discountPercentage}% OFF
                            </span>
                          )}
                          {activity.metadata.gameCount !== undefined && (
                            <span className="tag tag-games">
                              {activity.metadata.gameCount} Games
                            </span>
                          )}
                        </div>
                      )}

                      {/* Post actions for post_created activities */}
                      {activity.activityType === 'post_created' && activity.referenceId && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px',
                          marginTop: '12px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span style={{
                            color: 'var(--text-muted)',
                            fontSize: '0.95rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            ❤️ {activity.referenceId.likeCount || 0}
                          </span>
                          <span style={{
                            color: 'var(--text-muted)',
                            fontSize: '0.95rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            💬 {activity.referenceId.commentCount || 0}
                          </span>
                        </div>
                      )}

                      <Link 
                        to={getActivityLink(activity)}
                        className="feed-item-action"
                      >
                        View Details
                        <span className="action-arrow">→</span>
                      </Link>
                    </div>
                  </div>
                </article>
                </React.Fragment>
              ))}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerTarget} className="feed-loading-more">
                {loadingMore && (
                  <>
                    <div className="spinner-small" />
                    <span>Loading more...</span>
                  </>
                )}
              </div>
            )}

            {!hasMore && activities.length > 0 && (
              <div className="feed-end">
                <span>🎉</span>
                <p>You're all caught up!</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Right Sidebar - Trending/Suggestions */}
      <aside className="feed-right-sidebar">
        <div className="trending-card">
          <h3>🔥 Trending</h3>
          <div className="trending-list">
            <Link to="/giveaways" className="trending-item">
              <span className="trending-icon">🎁</span>
              <div>
                <div className="trending-title">Active Giveaways</div>
                <div className="trending-subtitle">Win amazing prizes!</div>
              </div>
            </Link>
            <Link to="/indie-games" className="trending-item">
              <span className="trending-icon">🎮</span>
              <div>
                <div className="trending-title">New Indie Games</div>
                <div className="trending-subtitle">Discover hidden gems</div>
              </div>
            </Link>
            <Link to="/discounts" className="trending-item">
              <span className="trending-icon">🏷️</span>
              <div>
                <div className="trending-title">Hot Discounts</div>
                <div className="trending-subtitle">Save on great games</div>
              </div>
            </Link>
          </div>
        </div>

        <div className="quick-actions-card">
          <h3>⚡ Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/create-list" className="quick-action-btn">
              <span>📝</span>
              Create List
            </Link>
            <Link to="/shop" className="quick-action-btn">
              <span>🛒</span>
              Shop
            </Link>
            <Link to="/achievements" className="quick-action-btn">
              <span>🏆</span>
              Achievements
            </Link>
            <Link to="/settings" className="quick-action-btn">
              <span>⚙️</span>
              Settings
            </Link>
          </div>
        </div>

        <div className="info-card">
          <h4>💡 Did you know?</h4>
          <p>You can earn points by engaging with the community, completing achievements, and participating in events!</p>
          <Link to="/points-guide" className="info-link">Learn more →</Link>
        </div>
      </aside>

      {/* Report Modal */}
      {reportModalOpen && (
        <div className="modal-overlay" onClick={() => !reportSubmitting && setReportModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Report Post</h2>
            
            {reportSuccess ? (
              <div className="success-message">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <p>Report submitted successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleReportSubmit}>
                {reportError && (
                  <div className="error-message">{reportError}</div>
                )}
                
                <div className="form-group">
                  <label>Reason *</label>
                  <select 
                    value={reportReason} 
                    onChange={(e) => setReportReason(e.target.value)}
                    required
                    disabled={reportSubmitting}
                  >
                    <option value="">Select a reason</option>
                    <option value="spam">Spam</option>
                    <option value="harassment">Harassment</option>
                    <option value="hate_speech">Hate Speech</option>
                    <option value="violence">Violence or Threats</option>
                    <option value="inappropriate_content">Inappropriate Content</option>
                    <option value="misinformation">Misinformation</option>
                    <option value="scam">Scam or Fraud</option>
                    <option value="impersonation">Impersonation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Please provide details about why you're reporting this post..."
                    rows="4"
                    maxLength="500"
                    required
                    disabled={reportSubmitting}
                  />
                  <small>{reportDescription.length}/500 characters</small>
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button" 
                    onClick={() => setReportModalOpen(false)}
                    disabled={reportSubmitting}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={reportSubmitting || !reportReason || !reportDescription.trim()}
                    className="btn-danger"
                  >
                    {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            )}
            
            <button 
              className="modal-close"
              onClick={() => !reportSubmitting && setReportModalOpen(false)}
              disabled={reportSubmitting}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;
